import { computed, nextTick, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { MEDIA_TYPE_VIDEO } from '@/js/constants';
import useConfig from '@/composables/useConfig';
import useR2RChat from '@/composables/useR2RChat';
import FontLoader from '@/js/fontLoader';

function oneOf(one, other) {
  return one === null ? one : one ?? other;
}

const content = ref({});

const assets = ref({});
const mainAssets = ref(null);

const currentContent = ref({});

const contentPromise = ref(null);
const contentPromiseResolve = ref(null);
const subtitle = ref('');
const hasMediaContent = computed(() => currentContent.value?.attrs?.qrCode
  || currentContent.value?.attrs?.headline?.value?.trim().length > 0
  || currentContent.value?.attrs?.text?.value?.trim().length > 0);
const isPaused = ref(false);

export default function useContent() {
  const { currentLanguage, activeAvatar, defaultStyles } = useConfig();
  const { hideAvatarDuringChat, isChatInProgress } = useR2RChat();

  const hideAvatar = computed(() => oneOf(
    currentContent.value?.attrs?.hideAvatar,
    defaultStyles.value.hideAvatar,
  ));
  const hideAvatarKeepDesign = computed(() => oneOf(
    currentContent.value?.attrs?.hideAvatarKeepDesign,
    defaultStyles.value.hideAvatarKeepDesign,
  ));
  const media = computed(() => oneOf(
    currentContent.value?.attrs?.media,
    defaultStyles.value.media,
  ));
  const vMedia = computed(() => oneOf(
    currentContent.value?.attrs?.vMedia,
    defaultStyles.value.vMedia,
  ));
  const useCenterVerticalDesktopDesign = computed(() => oneOf(
    currentContent.value?.attrs?.useCenterVerticalDesktopDesign,
    defaultStyles.value.useCenterVerticalDesktopDesign,
  ));

  const isHideAvatar = computed(() => hideAvatar.value
    || (isChatInProgress.value && hideAvatarDuringChat.value));
  const isKeepAvatarDesign = computed(() => isHideAvatar.value && hideAvatarKeepDesign.value);
  const isVideoMedia = computed(() => media.value?.type === MEDIA_TYPE_VIDEO);
  const isVVideoMedia = computed(() => vMedia.value?.type === MEDIA_TYPE_VIDEO);
  const isIndependentVerticalBg = computed(() => oneOf(
    currentContent.value?.attrs?.independentVerticalBg,
    defaultStyles.value.independentVerticalBg,
  ));

  const languageContent = computed(() => content.value[activeAvatar.value][currentLanguage.value]);

  function setContentData(avatarId, data) {
    const { content: contentData, assets: assetsData } = data;
    if (!mainAssets.value) {
      mainAssets.value = assetsData;
    }
    content.value[avatarId] = contentData;
    assets.value[avatarId] = assetsData;

    // load fonts after content is set
    if (data.config.fonts) {
      data.config.fonts.custom?.forEach((font) => {
        // eslint-disable-next-line
        const source = findContentAssetUrl(font, { customAvatarId: avatarId });
        FontLoader.loadFont(source);
      });

      FontLoader.loadGoogleFonts(data.config.fonts.google || []);
    }
  }

  function getById(id) {
    return languageContent.value[id];
  }

  function setCurrentContent(id) {
    currentContent.value = {
      uniqId: uuidv4(),
      id,
      ...languageContent.value[id],
    };
  }

  function resetCurrentContent() {
    currentContent.value = {};
  }

  function findContentAssetUrl(filename, { useMain = false, customAvatarId = null } = {}) {
    if (useMain) {
      return mainAssets.value?.content.find((url) => url.endsWith(filename));
    }
    const avatarId = customAvatarId ?? activeAvatar.value;
    return assets.value[avatarId]?.content.find((url) => url.endsWith(filename));
  }

  function isContentSupportStt() {
    return [
      'elements.PROJECT_MENU',
      'elements.QUESTION',
      'elements.FEEDBACK',
      'elements.KNOWLEDGE_BASE_UI',
    ].includes(currentContent.value.type)
    || (
      currentContent.value.type === 'elements.USER_INPUT'
        && currentContent.value.attrs.inputType !== 'canvas'
    );
  }

  function setSubtitle(text) {
    subtitle.value = text;
  }

  function hasContent(language) {
    return !!content.value[activeAvatar.value][language];
  }

  watch(() => currentContent.value.id, async () => {
    await nextTick();
    if (isVideoMedia.value) {
      contentPromise.value = new Promise((resolve) => {
        contentPromiseResolve.value = resolve;
      });
    }
  });

  function pauseContent() {
    isPaused.value = true;
  }

  function resumeContent() {
    isPaused.value = false;
  }

  return {
    setContentData,
    content,
    assets,
    languageContent,
    currentContent,
    isVideoMedia,
    isVVideoMedia,
    isIndependentVerticalBg,
    isHideAvatar,
    useCenterVerticalDesktopDesign,
    isKeepAvatarDesign,
    contentPromise,
    contentPromiseResolve,
    resetCurrentContent,
    setCurrentContent,
    findContentAssetUrl,
    isContentSupportStt,
    getById,
    setSubtitle,
    subtitle,
    hasContent,
    hasMediaContent,
    isPaused,
    pauseContent,
    resumeContent,
    media,
    vMedia,
  };
}
